import React, { useContext, useState, useMemo } from 'react';
import { Button, Col, Divider, Form, Input, message, Radio, Row, Select, Spin } from 'antd';
import PropTypes from 'prop-types';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { EnrollmentContext } from '../../../../providers/EnrollmentContextProvider';
import tokens from '../../../../utils/tokens';
import AloneCol from '../../../../components/common/AloneCol';
import formCol from '../../../../utils/formCol';
import useParams from '../../../params/useParams';
import paramType from '../../../params/paramEndpoints';
import select from '../../../../utils/select';
import currency from '../../../../utils/currency';
import form from '../../../../utils/form';
import EnrollmentService from '../../EnrollmentService';
import enrollmentAction from '../../../../providers/EnrollmentContextProvider/enrollmentAction';
import InputRut from '../../../../components/form/inputs/InputRut';
import { rutValidator } from '../../../../utils/validators';
import InputNumber from '../../../../components/form/inputs/InputNumber';
import useLocalParam from '../../../params/useLocalParam';
import { EnrollmentFormContext } from '../../../../providers/EnrollmentFormContextProvider';

const BankDataStep = ({ onFinish }) => {
  const { dispatch, postulantRut, enrollmentId } = useContext(EnrollmentContext);
  const { enrollmentForm } = useContext(EnrollmentFormContext);
  const [country, setCountry] = useState(
    enrollmentForm.getFieldValue(['person', 'address', 'country'])
  );
  const regionsPayload = useMemo(
    () => ({
      country,
    }),
    [country]
  );
  const [region, setRegion] = useState(
    enrollmentForm.getFieldValue(['person', 'address', 'region'])
  );
  const citiesPayload = useMemo(
    () => ({
      region,
    }),
    [region]
  );
  const [countries, areCountriesLoading] = useParams(paramType.COUNTRIES);
  const [regions, areRegionsLoading] = useLocalParam(paramType.REGIONS, regionsPayload);
  const [cities, areCitiesLoading] = useLocalParam(paramType.CITIES, citiesPayload);
  const [banks, areBanksLoading] = useParams(paramType.BANKS);
  const [bankAccountTypes, areBankAccountTypesLoading] = useParams(paramType.BANK_ACCOUNT_TYPES);
  const [monthlyIncomeRanges] = useParams(paramType.MONTHLY_INCOME_RANGES);
  const [currencies, areCurrenciesLoading] = useParams(paramType.CURRENCIES);
  const [accountPurposes, areAccountPurposesLoading] = useParams(paramType.ACCOUNT_PURPOSES);
  const [workerTypes, areWorkerTypesLoading] = useParams(paramType.WORKER_TYPES);
  const [isLoading, setLoading] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [showsOtherPurpose, setShowsOtherPurpose] = useState(
    enrollmentForm.getFieldValue(['inancialData', 'accountPurposeId']) === 'OTRO'
  );
  const [showsOtherWorkerType, setShowsOtherWorkerType] = useState(
    enrollmentForm.getFieldValue(['laboralBackground', 'workerType']) === 'OTRO'
  );
  const [isDependentWorker, setDependentWorker] = useState(
    enrollmentForm.getFieldValue(['laboralBackground', 'workerType']) === 'DEPENDENT'
  );

  const updateMonthlyInvestment = () =>
    enrollmentForm.setFieldsValue({
      monthlyInvestment: enrollmentForm.getFieldValue('monthlyInvestment'),
    });

  const handleCurrencySelect = (currencyId) => {
    const selectedCurrency = currencies.find((currencyType) => currencyType.id === currencyId);

    setCurrencySymbol(selectedCurrency.symbol);

    updateMonthlyInvestment();
  };

  const handleWorkerTypeSelect = (value) => {
    setDependentWorker(value === 'DEPENDENT');
    setShowsOtherWorkerType(value === 'OTRO');
  };

  const handleAccountPurposeChange = (value) => {
    setShowsOtherPurpose(value === 'OTRO');
  };

  const handleSubmit = (values) => {
    const {
      accountPurposeId,
      otherAccountPurpose,
      financialData,
      workerType,
      otherWorkerType,
      bankAccounts,
      laboralAddress,
      bankData,
      laboralBackground,
      ...restValues
    } = values;
    if (!bankData || !bankData.bankAccounts || bankData.bankAccounts.length === 0) {
      message.error('Necesita agregar al menos una cuenta bancaria.');
    } else {
      setLoading(true);

      let laboral = laboralBackground
        ? {
            workerType,
            otherWorkerType,
            ...laboralBackground,
          }
        : {};

      if (values.laboralBackGround) {
        laboral = { ...laboral, ...values.laboralBackGround };
      }

      let payload = {
        bankData: {
          accountPurposeId: showsOtherPurpose ? null : accountPurposeId,
          otherAccountPurpose: showsOtherPurpose ? otherAccountPurpose : null,
          monthlyInvestment: financialData.monthlyInvestment
            ? Number(financialData.monthlyInvestment.replace(/\./g, ''))
            : 0,

          monthlyIncomeRangeId: financialData.monthlyIncomeRangeId,
          workerType,
          otherWorkerType,
          bankAccounts: bankData.bankAccounts
            ? bankData.bankAccounts.map((bankAccount) => ({
                ...bankAccount,
                number: Number(bankAccount.number),
                numberValidation: undefined,
              }))
            : [],
          laboralAddress: laboralAddress
            ? {
                ...laboralAddress,
                region: laboralAddress?.region || null,
                city: laboralAddress?.city || null,
              }
            : undefined,
          ...restValues,
        },
        laboralBackground: laboralBackground && {
          workerType,
          otherWorkerType,
          ...laboralBackground,
        },
      };

      payload = {
        ...payload,
        laboralBackground: payload.laboralBackground && {
          ...payload.laboralBackground,
          ...laboral,
          position: values.position,
          professionJob: values.professionJob,
        },
      };

      EnrollmentService.updateEnrollment(payload, enrollmentId)
        .then(() => {
          dispatch({ type: enrollmentAction.SET_STEP_DATA, payload: values });
          onFinish();
        })
        .catch(() => {
          message.error('Error inesperado. Por favor, intente nuevamente.');
        })
        .finally(() => setLoading(false));
    }
  };

  const handleLaboralCountrySelect = (value) => {
    setCountry(value);
    setRegion(undefined);
    enrollmentForm.setFieldsValue({
      laboralAddress: {
        region: undefined,
        city: undefined,
      },
    });
  };

  const handleLaboralRegionSelect = (value) => {
    setRegion(value);
    enrollmentForm.setFieldsValue({
      laboralAddress: {
        city: undefined,
      },
    });
  };

  const employerRutValidator = async (_, value) => {
    await rutValidator(_, value);
    if (value === postulantRut) {
      throw new Error('No puedes ingresar el mismo RUT del solicitante.');
    }
  };

  return (
    <Spin spinning={isLoading}>
      <Row justify="center">
        <Col {...formCol.responsive}>
          <Form
            layout="vertical"
            onFinish={handleSubmit}
            form={enrollmentForm}
            validateMessages={form.validateMessages}
          >
            <p>
              <b>Datos Bancarios</b>
            </p>
            <Row gutter={tokens.forms.horizontalGutterInPx}>
              <Col span={24}>
                <Form.List name={['bankData', 'bankAccounts']}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, fieldKey, ...restField }, index) => (
                        <Row key={key} gutter={tokens.forms.horizontalGutterInPx}>
                          <Col span={24}>{index > 0 && <Divider style={{ marginTop: 0 }} />}</Col>
                          <Col span={24}>
                            <Form.Item
                              {...restField}
                              name={[name, 'bank']}
                              fieldKey={[fieldKey, 'bankId']}
                              rules={[{ required: true }]}
                              label="Banco"
                            >
                              <Select
                                loading={areBanksLoading}
                                filterOption={select.filterOptions.searchByLabel}
                                showSearch
                              >
                                {banks.map((bank) => (
                                  <Select.Option key={bank.id} value={bank.id}>
                                    {bank.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <AloneCol span={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'type']}
                              fieldKey={[fieldKey, 'type']}
                              label="Tipo de cuenta"
                              rules={[{ required: true }]}
                            >
                              <Select
                                loading={areBankAccountTypesLoading}
                                filterOption={select.filterOptions.searchByLabel}
                                showSearch
                              >
                                {bankAccountTypes.map((bankAccountType) => (
                                  <Select.Option
                                    key={bankAccountType.id}
                                    value={bankAccountType.id}
                                  >
                                    {bankAccountType.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </AloneCol>
                          <AloneCol span={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'currency']}
                              fieldKey={[fieldKey, 'currency']}
                              label="Moneda"
                              rules={[{ required: true }]}
                            >
                              <Select
                                placeholder="Seleccione Moneda"
                                filterOption={select.filterOptions.searchByLabel}
                                loading={areCurrenciesLoading}
                                showSearch
                              >
                                {currencies.map((currencyType) => (
                                  <Select.Option key={currencyType.id} value={currencyType.id}>
                                    {currencyType.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </AloneCol>
                          <Col span={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'number']}
                              fieldKey={[fieldKey, 'number']}
                              label="Número de cuenta"
                              normalize={form.normalizers.numeric}
                              rules={[{ required: true }]}
                            >
                              <Input maxLength={20} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              {...restField}
                              name={[name, 'numberValidation']}
                              fieldKey={[fieldKey, 'numberValidation']}
                              label="Reescriba su cuenta"
                              normalize={form.normalizers.numeric}
                              validateTrigger="onChange"
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (
                                      value !==
                                      enrollmentForm.getFieldValue([
                                        'bankData',
                                        'bankAccounts',
                                        name,
                                        'number',
                                      ])
                                    ) {
                                      return Promise.reject(new Error('Las cuentas no coinciden'));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Input maxLength={20} {...form.inputProps.noPaste} />
                            </Form.Item>
                          </Col>
                          {fields.length > 1 && index > 0 && (
                            <Col span={24}>
                              <Form.Item>
                                <Button
                                  type="dashed"
                                  onClick={() => remove(name)}
                                  icon={<MinusOutlined />}
                                >
                                  Eliminar cuenta
                                </Button>
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                      ))}
                      {fields.length < 4 && (
                        <Form.Item>
                          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                            Agregar cuenta
                          </Button>
                        </Form.Item>
                      )}
                    </>
                  )}
                </Form.List>
              </Col>

              <Col span={24}>
                <p>
                  <b>Datos Financieros</b>
                </p>
              </Col>
              <Col span={24}>
                <Form.Item
                  name={['financialData', 'monthlyIncomeRangeId']}
                  label="Selecciona tus ingresos mensuales (en pesos)"
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Row>
                      {monthlyIncomeRanges.map(({ minAmount, maxAmount, id }) => {
                        let label;

                        if (!minAmount) {
                          label = `Menos de ${currency.format(maxAmount)}`;
                        } else if (!maxAmount) {
                          label = `Más de ${currency.format(minAmount)}`;
                        } else {
                          label = `Entre ${currency.format(minAmount)} y ${currency.format(
                            maxAmount
                          )}`;
                        }

                        return (
                          <Col key={id} span={24}>
                            <Radio value={id}>{label}</Radio>
                          </Col>
                        );
                      })}
                    </Row>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Monto estimado a operar mensual o monto potencial estimado a invertir"
                  rules={[{ required: true }]}
                  style={{ margin: 0 }}
                >
                  <Row gutter={tokens.forms.horizontalGutterInPx}>
                    <Col span={12}>
                      <Form.Item name="currency" rules={[{ required: true }]}>
                        <Select
                          placeholder="Seleccione Moneda"
                          onChange={handleCurrencySelect}
                          filterOption={select.filterOptions.searchByLabel}
                          loading={areCurrenciesLoading}
                          showSearch
                        >
                          {currencies.map((currencyType) => (
                            <Select.Option key={currencyType.id} value={currencyType.id}>
                              {currencyType.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['financialData', 'monthlyInvestment']}
                        normalize={form.normalizers.numberWithThousand}
                        rules={[{ required: true }]}
                      >
                        <Input
                          maxLength={40}
                          placeholder="Ingresar monto"
                          prefix={currencySymbol}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Propósito del uso de la relación comercial"
                  required
                  style={{ margin: 0 }}
                >
                  <Row gutter={tokens.forms.horizontalGutterInPx}>
                    <Col span={12}>
                      <Form.Item rules={[{ required: true }]} name="accountPurposeId">
                        <Select
                          loading={areAccountPurposesLoading}
                          onChange={handleAccountPurposeChange}
                        >
                          {accountPurposes.map(({ id, name }) => (
                            <Select.Option key={id} value={id}>
                              {name}
                            </Select.Option>
                          ))}
                          <Select.Option value="OTRO">Otra relación (especificar)</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      {showsOtherPurpose && (
                        <Form.Item
                          name={['financialData', 'otherAccountPurpose']}
                          rules={[{ required: true }]}
                        >
                          <Input maxLength={24} placeholder="Especifique" />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col span={24}>
                <p>
                  <b>Antecedentes Laborales</b>
                </p>
              </Col>
              <Col span={24}>
                <Form.Item label="Tipo de trabajador" style={{ margin: 0 }} required>
                  <Row gutter={tokens.forms.horizontalGutterInPx}>
                    <Col span={12}>
                      <Form.Item
                        name={['laboralBackground', 'workerType']}
                        rules={[{ required: true }]}
                      >
                        <Select
                          loading={areWorkerTypesLoading}
                          onChange={handleWorkerTypeSelect}
                          placeholder="Seleccionar"
                        >
                          {workerTypes.map(({ id, name }) => (
                            <Select.Option key={id} value={id}>
                              {name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      {showsOtherWorkerType && (
                        <Form.Item
                          name={['laboralBackground', 'otherWorkerType']}
                          rules={[{ required: true }]}
                        >
                          <Input placeholder="Dueño/a de casa, pensionado, etc." maxLength={32} />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              {isDependentWorker && (
                <>
                  <AloneCol span={12}>
                    <Form.Item
                      name={['laboralBackGround', 'employerName']}
                      label="Nombre o Razón Social del Empleador"
                      rules={[{ required: true }]}
                    >
                      <Input maxLength={32} />
                    </Form.Item>
                  </AloneCol>
                  <AloneCol span={12}>
                    <Form.Item
                      name={['laboralBackGround', 'rut']}
                      label="RUT"
                      rules={[{ required: true, validator: employerRutValidator }]}
                    >
                      <InputRut maxLength={13} />
                    </Form.Item>
                  </AloneCol>
                  <Col span={24}>
                    <p>
                      <b>Dirección Laboral</b>
                    </p>
                    <Row gutter={tokens.forms.horizontalGutterInPx}>
                      <Col span={12}>
                        <Form.Item
                          name={['laboralBackground', 'laboralAddress', 'country']}
                          label="País"
                          rules={[{ required: true }]}
                        >
                          <Select
                            placeholder="Seleccionar"
                            loading={areCountriesLoading}
                            filterOption={select.filterOptions.searchByLabel}
                            onChange={handleLaboralCountrySelect}
                          >
                            {countries.map(({ id, name }) => (
                              <Select.Option key={id} value={id}>
                                {name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={['laboralBackground', 'laboralAddress', 'region']}
                          label="Región"
                          rules={[
                            {
                              required: country && regions.length > 0,
                            },
                          ]}
                        >
                          <Select
                            placeholder="Seleccionar"
                            loading={areRegionsLoading}
                            onChange={handleLaboralRegionSelect}
                            disabled={!country || regions.length === 0}
                          >
                            {regions.map(({ id, name }) => (
                              <Select.Option key={id} value={id}>
                                {name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name={['laboralBackground', 'laboralAddress', 'city']}
                          label="Comuna"
                          rules={[
                            {
                              required: region && cities.length > 0,
                            },
                          ]}
                        >
                          <Select
                            placeholder="Seleccionar"
                            loading={areCitiesLoading}
                            disabled={!region || regions.length === 0}
                          >
                            {cities.map(({ id, name }) => (
                              <Select.Option key={id} value={id}>
                                {name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={17}>
                        <Form.Item
                          name={['laboralBackground', 'laboralAddress', 'streetName']}
                          label="Calle/Avenida"
                          rules={[{ required: true }]}
                        >
                          <Input maxLength={32} autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item
                          name={['laboralBackground', 'laboralAddress', 'streetNumber']}
                          label="Número"
                          rules={[{ required: true }]}
                        >
                          <InputNumber hideArrows maxLength={8} autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={['laboralBackground', 'laboralAddress', 'houseNumber']}
                          label="Departamento / Oficina"
                        >
                          <Input maxLength={10} autoComplete="off" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="position" label="Cargo" rules={[{ required: true }]}>
                      <Input maxLength={64} />
                    </Form.Item>
                  </Col>
                </>
              )}
              <AloneCol span={24}>
                <Form.Item
                  name="professionJob"
                  label="Profesión u Oficio"
                  rules={[{ required: true }]}
                >
                  <Input maxLength={64} />
                </Form.Item>
              </AloneCol>
              <Col span={24}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block>
                    Siguiente
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Spin>
  );
};

BankDataStep.propTypes = {
  onFinish: PropTypes.func.isRequired,
};

export default BankDataStep;
